import {useState} from 'react'
import {z} from 'zod'
import {useAuth} from 'features/common/context/auth'

const signupSchema = z.object({
    email: z.string().email({message: 'VALIDATION_INVALID_EMAIL'}),
    password: z.string(),
})

type SigninData = z.infer<typeof signupSchema>
type SigninErrors = Partial<Record<keyof SigninData, string>>

type OnChangeHandler = (args: {
    prop: keyof SigninData
    value: SigninData[keyof SigninData]
}) => void

const emptyData: SigninData = {
    email: '',
    password: '',
} as const

const useSignin = () => {
    const {login, loginError, loginLoading} = useAuth()
    const [data, setData] = useState<SigninData>(emptyData)
    const [errors, setErrors] = useState<SigninErrors>({})

    const onChange: OnChangeHandler = ({prop, value}) => {
        setData((sd) => {
            const newData = {...sd, [prop]: value}
            const parseRes = signupSchema.safeParse(newData)

            if (!parseRes.success) {
                const formattedErrors = parseRes.error.flatten()
                const currentError = formattedErrors.fieldErrors[prop]
                setErrors((errs) => {
                    if (currentError) {
                        return {
                            ...errs,
                            [prop]: currentError[0],
                        }
                    }
                    const newErrs = {...errs}
                    delete newErrs[prop]
                    return newErrs
                })
            } else {
                setErrors({})
            }

            return newData
        })
    }

    const onSubmit = () => {
        login && login(data)
    }

    return {
        data,
        errors,
        onChange,
        onSubmit,
        onSubmitError: loginError,
        loading: loginLoading,
    }
}

export default useSignin
